/**
 * Soft-safe Move 3: Summary / Share step panel.
 * Soft-safe ①: shareViewModel is the sole human face for Share UI chronology.
 * Soft #2 / UX 4: screen order line → holes → board. Soft-refuse: identity lock held (no metric Soft UI copy). Soft 14 ops-only.
 * Soft-safe ③ / Growth 7·8: SoftGate/toast must not leak into share payload.
 * Soft 14 ops-only — no FAQ / crawl fill here.
 * Soft-outside 2.3: Share card is L1 protagonist; Takeaway/Add stay Quiet details under card.
 * Soft Quiet C1: process chronology behind 「과정 더 보기」 (default closed; not in Copy/PNG).
 * Soft D: `#/h/` received Quiet re-record CTA → blank new record only (Soft-safe ⑤; Soft 6 ≤ L1 after card).
 * Soft E P4: processMore denser timeline shows seat name (BTN), never Hero label.
 */
import {
  t,
} from '../../i18n/strings'
import { fitSavedMarks, isImageSavedMarked } from '../savedMarker'
import { dotSegments } from '../dotSegments'
import {
  formatEndingLine,
} from '../../lib/endingRecord'
import {
  actorsMatch,
  buildProNotation,
  formatBoardProSpaced,
  formatHolePro,
} from '../../lib/notation'
import {
  boardMismatchWarnings,
  formatCards,
  formatHolePrettyLine,
  formatPotDisplayForHand,
  formatSeatCode,
  getPositions,
  handShareMetaHead,
  handLineText,
  listKnownHoleEntries,
  shareOffersOptionalVillainHolesCta,
} from '../../lib/poker'
import {
  shareProcessKind,
  shareProcessLineText,
} from '../../lib/potShape'
import {
  formatShareReviewedLine,
} from '../../lib/shareStamp'
import {
  canShareFiles,
  canShareText,
} from '../../lib/shareText'
import {
  buildShareViewModel,
} from '../../lib/shareViewModel'
import {
  getState,
  handLooksIncomplete,
  isReceivedFromLink,
  resetHand,
  setCaptureMode,
  setHolesMode,
  setTakeaway,
} from '../../state'
import {
  playingCard,
} from '../cards'
import {
  el,
} from '../dom'
import {
  explainOnly,
  go,
  languageSwitcher,
  requestStep,
} from './helpers'
import {
  copySummary,
  onShareText,
  onSaveShareImage,
  onShareShareImage,
  copyHandLink,
  requestCaptureOn,
} from './shareIntents'

export function renderSummary(): HTMLElement {
  const ui = t()
  const { hand, toast } = getState()
  const proText = buildProNotation(hand)
  const holePro = formatHolePro(hand.holeCards)
  // Soft-safe Move 1: Share UI consumes share view-model (layers + chronology).
  const svm = buildShareViewModel(hand)
  const boardCards = svm.boardCards

  const shareTextAvailable = canShareText()
  const holeProLine = holePro.full
    ? holePro.shorthand
      ? `${holePro.full} (${holePro.shorthand})`
      : holePro.full
    : ''
  const boardPro = formatBoardProSpaced(hand)
  const copyLabel = toast === 'copied' ? ui.copied : ui.copyBoth
  // Soft Ultimate #4: PNG save button confirms like Copy
  const saveLabel = toast === 'imageSaved' ? ui.imageSaved : ui.saveShareImage
  // Soft residual 6: Quiet in-app confirm for OS share success (not louder than Soft 4)
  const shareTextLabel = toast === 'shared' ? ui.shared : ui.shareText
  const shareImageLabel =
    toast === 'imageShared' ? ui.imageShared : ui.shareImage

  const metaExtraBits: string[] = []
  if (
    hand.effectiveStackBb != null &&
    Number.isFinite(hand.effectiveStackBb)
  ) {
    metaExtraBits.push(`eff ${hand.effectiveStackBb}bb`)
  }
  const potDispShare = formatPotDisplayForHand(hand)
  if (potDispShare.combinedPro) metaExtraBits.push(potDispShare.combinedPro)

  const holeEntries = listKnownHoleEntries(hand, { includeEmptyHero: true })
  const villainEntries = holeEntries.filter((e) => !e.isHero)

  const handIncomplete = handLooksIncomplete(hand)
  // Soft G: optional ending — one meta-size line after the last process line.
  const endingLine = formatEndingLine(hand, ui)
  const processKind = shareProcessKind(hand, getPositions(hand.playerCount))
  const processLineText = shareProcessLineText(processKind, ui)

  // Meta = single L3 chrome line (table · hero/stack · pot extras).
  // Keep hole faces as the only competing L1 — no dual hero headline.
  const metaChromeBits = [
    // Softwise M10d: one settings line shared with the PNG.
    handShareMetaHead(hand),
    ...metaExtraBits,
  ]

  // Brand/incomplete stay at top; table meta + process line live in the foot
  // so Actions → holes → board (L1) lead the card (Soft #2 tidy).
  const shareCard = el('div', { className: 'shareCard' }, [
    el('div', { className: 'shareCardTop' }, [
      el('div', { className: 'shareCardBrand', text: ui.brand }),
      handIncomplete
        ? el('div', {
            className: 'shareIncompleteBadge',
            text: ui.incompleteBadge,
          })
        : null,
    ]),
    // Soft empty-layer: hide L1.1 when no line — never fabricate "—"
    svm.layers.line
      ? el('div', { className: 'shareSection shareSectionActions' }, [
          el('div', { className: 'shareLabel', text: ui.summaryActions }),
          // Softwise M10b: the single line generator — same text as the
          // input stacked line, Copy and PNG. Layout order unchanged.
          el('div', {
            className: 'summaryActions shareLineText',
          }, [el('span', { className: 'dotLine' }, dotSegments([handLineText(hand)]))]),
        ])
      : null,
    // Soft Quiet C1: 「과정 더 보기」 — street chronology only, default closed.
    // Soft-safe ③: weight via CSS weight-* only — no actionCount digits / title %.
    svm.timeline.segments.length
      ? el(
          'details',
          {
            className: 'processMore',
            attrs: {
              'data-peak': svm.timeline.peakWeight ?? '',
            },
          },
          [
            el('summary', {
              className: 'processMoreSummary',
              text: ui.processMoreSummary,
            }),
            el(
              'div',
              {
                className: 'processMoreBody processTimeline',
                attrs: { 'aria-label': ui.timelineProcess },
              },
              svm.timeline.segments.map((seg) =>
                el('div', {
                  className: `processTimelineSeg weight-${seg.weight}`,
                  attrs: {
                    'data-street': seg.street,
                    'data-weight': seg.weight,
                  },
                }, [
                  el('div', {
                    className: 'processTimelineStreet',
                    text: ui.street[seg.street],
                  }),
                  seg.board.length
                    ? el('div', {
                        className: 'processTimelineBoard',
                        text: ui.timelineBoard(formatCards(seg.board)),
                      })
                    : null,
                  ...seg.actions.map((a) => {
                    const size = a.size ? ` ${a.size}` : ''
                    // Soft E P4: label-only — Hero → seat name (BTN etc.); Soft 6 layout unchanged.
                    const seat = actorsMatch(a.actor, 'Hero', hand.heroPosition)
                      ? formatSeatCode(hand.heroPosition, hand.playerCount)
                      : a.actor.trim()
                    return el('div', {
                      className: 'processTimelineAction',
                      text: `· ${seat}: ${ui.actionType[a.type]}${size}`,
                    })
                  }),
                ]),
              ),
            ),
          ],
        )
      : null,
        // Soft empty-layer: L1.0 holes only when present
    svm.layers.holes
      ? el('div', { className: 'shareSection' }, [
      el('div', { className: 'shareLabel', text: ui.summaryHole }),
      el('div', { className: 'shareSeatBlock' }, [
        el('div', {
          className: 'shareSeatLabel',
          text: formatSeatCode(hand.heroPosition, hand.playerCount),
        }),
        el('div', { className: 'shareCards' }, [
          playingCard({ card: hand.holeCards[0], large: true }),
          playingCard({ card: hand.holeCards[1], large: true }),
        ]),
        holeProLine
          ? el('div', { className: 'proInline', text: holeProLine })
          : null,
      ]),
      ...villainEntries.map((e) => {
        const pretty = formatHolePrettyLine(e.hole)
        const pro = formatHolePro(e.hole)
        const proLine = pro.full
          ? pro.shorthand
            ? `${pro.full} (${pro.shorthand})`
            : pro.full
          : ''
        return el('div', { className: 'shareSeatBlock' }, [
          el('div', { className: 'shareSeatLabel', text: e.label }),
          el('div', { className: 'shareCards' }, [
            playingCard({ card: e.hole[0], large: true }),
            playingCard({ card: e.hole[1], large: true }),
          ]),
          proLine
            ? el('div', { className: 'proInline', text: proLine })
            : pretty
              ? el('div', { className: 'proInline', text: pretty })
              : null,
        ])
      }),
    ])
      : null,

    // Soft empty-layer: hide L1.2 board when empty — never "—" placeholder
    svm.layers.board && boardCards.length
      ? el('div', { className: 'shareSection' }, [
          el('div', { className: 'shareLabel', text: ui.summaryBoard }),
          el(
            'div',
            { className: 'shareCards' },
            boardCards.map((c) => playingCard({ card: c, large: true })),
          ),
          boardPro
            ? el('div', { className: 'proInline', text: boardPro })
            : null,
        ])
      : null,
    svm.takeaway
      ? el('div', { className: 'shareSection shareSectionTakeaway' }, [
          el('div', { className: 'shareLabel', text: ui.takeawayLabel }),
          el('div', {
            className: 'shareTakeawayText',
            text: svm.takeaway,
          }),
        ])
      : null,
    // Soft G (B/C/D): one line right after the last process line, meta-line
    // size; no section / box / icon / colour. Empty → nothing rendered.
    endingLine
      ? el('div', { className: 'shareEndingLine', text: endingLine })
      : null,
    el('details', { className: 'shareSection shareSectionPro' }, [
      el('summary', { className: 'shareLabel shareProSummary', text: ui.summaryProLabel }),
      el('pre', { className: 'sharePro', text: proText }),
    ]),
    el('div', { className: 'shareCardFoot' }, [
      el('div', { className: 'shareMetaLine' }, dotSegments([metaChromeBits.join(' · ')])),
      !handIncomplete && processLineText
        ? el('div', {
            className: 'muted shareProcessLine',
            text: processLineText,
          })
        : null,
      !handIncomplete
        ? el('div', {
            className: 'muted shareReviewedStamp',
            text: formatShareReviewedLine(ui.shareReviewedLabel),
          })
        : null,
      el('div', { className: 'shareFooter', text: ui.summaryFooter }),
    ]),
  ])

  const boardWarns = boardMismatchWarnings(hand)
  const { captureMode, holesMode } = getState()
  const heroOnlyNoVillains =
    holesMode === 'heroOnly' && villainEntries.length === 0
  const offerVillainHolesCta =
    heroOnlyNoVillains &&
    shareOffersOptionalVillainHolesCta(hand, holesMode, false)
  // Softwise M10f: drop the hero-only-holes note copy; keep optional
  // Add-villains CTA only (summary head hero seat label stays — settings, not line).
  const heroOnlyHolesNoteBlock = offerVillainHolesCta
    ? el('div', { className: 'heroOnlyHolesNoteBlock' }, [
        el('button', {
          className: 'btn btnCompact btnQuiet shareAddVillainsCta',
          attrs: { type: 'button' },
          text: ui.heroCardsAddVillainsCta,
          on: {
            click: () => {
              setHolesMode('withVillains')
              requestStep('cards')
            },
          },
        }),
      ])
    : null

  const captureToggle = el('button', {
    className: [
      'btn',
      'summaryCaptureBtn',
      captureMode ? 'btnPrimary' : 'btnQuiet',
    ]
      .filter(Boolean)
      .join(' '),
    attrs: {
      type: 'button',
      'aria-pressed': captureMode ? 'true' : 'false',
    },
    text: captureMode ? ui.captureExit : ui.captureMode,
    on: { click: () => requestCaptureOn() },
  })

  // Softwise M9 Share Quiet Soft 6: 「채팅에 공유」/copyBoth is Quiet secondary —
  // visual weight < L1 shareCard (no btnPrimary / full-bleed white). Softwise M4
  // stack kept: one chat CTA outside details; Copy/PNG/link stay under shareMoreChrome.
  const toolbar = el('div', { className: 'summaryToolbar' }, [
    el('div', { className: 'summaryToolbarTop' }, [
      languageSwitcher('compact'),
      el('button', {
        className: 'btn',
        attrs: { type: 'button' },
        text: ui.prev,
        on: { click: () => go(-1) },
      }),
      el('button', {
        className: 'btn btnGhost summaryNewHandBtn',
        attrs: { type: 'button' },
        text: ui.newHand,
        on: { click: () => resetHand() },
      }),
    ]),
    el('div', { className: 'shareRow shareRowPrimary' }, [
      el('button', {
        className: 'btn btnQuiet sharePrimaryBtn',
        attrs: { type: 'button', title: ui.summaryCopyLabel },
        text: copyLabel,
        on: { click: () => void copySummary('both') },
      }),
    ]),
    el(
      'details',
      { className: 'shareMoreChrome' },
      [
        el('summary', { className: 'shareMoreSummary', text: ui.shareMoreSummary }, [
          // Toast PR: screen-only confirmation after a successful image save.
          isImageSavedMarked()
            ? (queueMicrotask(fitSavedMarks),
              el('span', { className: 'shareSavedMark', text: `· ${ui.shareSavedMark}` }))
            : null,
        ]),
        el('div', { className: 'shareMoreBody' }, [
          captureToggle,
          shareTextAvailable
            ? el('button', {
                className: 'btn btnQuiet',
                attrs: { type: 'button' },
                text: shareTextLabel,
                on: { click: () => void onShareText() },
              })
            : null,
          el('button', {
            className: 'btn btnQuiet',
            attrs: { type: 'button' },
            text: saveLabel,
            on: { click: () => void onSaveShareImage() },
          }),
          canShareFiles()
            ? el('button', {
                className: 'btn btnQuiet',
                attrs: { type: 'button' },
                text: shareImageLabel,
                on: { click: () => void onShareShareImage() },
              })
            : null,
          el('button', {
            className: 'btn btnQuiet shareSecondaryBtn',
            attrs: { type: 'button', title: ui.copyLinkTitle },
            text: ui.copyLink,
            on: { click: () => void copyHandLink() },
          }),
          el('div', { className: 'copyModeRow', attrs: { role: 'group', 'aria-label': ui.summaryCopyLabel } }, [
            el('button', {
              className: 'btn btnCompact btnQuiet',
              attrs: { type: 'button' },
              text: ui.copyHuman,
              on: { click: () => void copySummary('human') },
            }),
            el('button', {
              className: 'btn btnCompact btnQuiet',
              attrs: { type: 'button' },
              text: ui.copyPro,
              on: { click: () => void copySummary('pro') },
            }),
          ]),
          el('p', { className: 'muted shareActionsHint', text: ui.screenshotHint }),
        ]),
      ],
    ),
  ])

  const captureExit =
    captureMode
      ? el('div', { className: 'captureExitBar' }, [
          el('button', {
            className: 'btn btnPrimary',
            attrs: { type: 'button' },
            text: ui.captureExit,
            on: { click: () => setCaptureMode(false) },
          }),
        ])
      : null

  // Soft 4 Quiet: open Takeaway/Add only when user already has a takeaway line.
  // Hero-only holes note must NOT force-open (stays inside collapsed details).
  const secondaryChromeOpen = Boolean(!handIncomplete && hand.takeaway.trim())
  // Soft residual 8: empty-state induction on collapsed summary only (Soft #2 stays closed)
  const secondaryInvite =
    !handIncomplete && !hand.takeaway.trim()
  const secondarySummaryText = secondaryInvite
    ? ui.shareSecondaryInvite
    : ui.shareSecondarySummary

  return el('section', { className: 'panel panelSummary' }, [
    captureExit,
    el('h2', {
      className: 'panelTitle',
      attrs: { tabindex: -1 },
      text: ui.step.summary,
    }),
    explainOnly(el('p', { className: 'panelHint summaryHintOut', text: ui.summaryHint })),
    ...boardWarns.map((w) =>
      el('p', { className: 'boardWarn', text: w }),
    ),
    shareCard,
    // Soft D Soft 6: Quiet re-record CTA after L1 .shareCard only (never before; ≤ L1 weight).
    // Soft-safe ⑤: resetHand → blank new record — never imports received `#/h/` hand.
    isReceivedFromLink()
      ? el('div', { className: 'receivedQuietRerecord' }, [
          el('button', {
            className: 'btn btnQuiet receivedQuietRerecordCta',
            attrs: { type: 'button' },
            text: ui.receivedQuietRerecordCta,
            on: {
              click: () => {
                // Soft D Soft-safe ⑤ FAIL lock: blank only — never import received hand state.
                resetHand()
              },
            },
          }),
        ])
      : null,
    // Soft residual 2: secondary edits collapsed — card stays L1 protagonist
    el(
      'details',
      {
        className: 'shareSecondaryChrome',
        attrs: secondaryChromeOpen ? { open: '' } : undefined,
      },
      [
      el('summary', {
        className: secondaryInvite
          ? 'shareSecondarySummary shareSecondaryInvite'
          : 'shareSecondarySummary',
        text: secondarySummaryText,
      }),
      !handIncomplete
        ? el('div', { className: 'shareTakeawayEditor' }, [
            el('label', { className: 'field' }, [
              el('span', { className: 'label', text: ui.takeawayLabel }),
              el('input', {
                className: 'input',
                attrs: {
                  type: 'text',
                  maxlength: 160,
                  autocomplete: 'off',
                  spellcheck: 'true',
                  value: hand.takeaway,
                  placeholder: ui.takeawayPlaceholder,
                  'aria-label': ui.takeawayLabel,
                },
                on: {
                  input: (e) => {
                    const value = (e.target as HTMLInputElement).value
                    setTakeaway(value, { silent: true })
                    const trimmed = value.trim()
                    const field = (e.target as HTMLInputElement).closest(
                      '.field',
                    ) as HTMLElement | null
                    // Soft #3 livefix: hide/show process nudge immediately (silent input skips render)
                    let nudge = field?.querySelector(
                      '.takeawayNudge',
                    ) as HTMLElement | null
                    let inLink = field?.querySelector(
                      '.takeawayInLinkFact',
                    ) as HTMLElement | null
                    if (trimmed) {
                      nudge?.remove()
                      // Soft I2b: fact line with the first non-empty takeaway (sender only).
                      if (field && !inLink && !isReceivedFromLink()) {
                        field.appendChild(
                          el('p', {
                            className: 'muted takeawayInLinkFact',
                            text: ui.takeawayInLinkFact,
                          }),
                        )
                      }
                    } else {
                      inLink?.remove()
                      if (field && !nudge) {
                        field.appendChild(
                          el('p', { className: 'muted takeawayNudge' }, nudgeSegments(ui.takeawayNudge)),
                        )
                      }
                    }
                    let section = document.querySelector(
                      '.shareSectionTakeaway',
                    ) as HTMLElement | null
                    if (!trimmed) {
                      section?.remove()
                      return
                    }
                    if (!section) {
                      // Soft G: keep takeaway before the ending line (order C).
                      const pro =
                        document.querySelector('.shareEndingLine') ??
                        document.querySelector('.shareSectionPro')
                      if (!pro?.parentElement) return
                      section = el(
                        'div',
                        { className: 'shareSection shareSectionTakeaway' },
                        [
                          el('div', {
                            className: 'shareLabel',
                            text: ui.takeawayLabel,
                          }),
                          el('div', {
                            className: 'shareTakeawayText',
                            text: trimmed,
                          }),
                        ],
                      )
                      pro.parentElement.insertBefore(section, pro)
                      return
                    }
                    const card = section.querySelector('.shareTakeawayText')
                    if (card) card.textContent = trimmed
                  },
                  change: (e) => {
                    setTakeaway((e.target as HTMLInputElement).value)
                  },
                },
              }),
              // Soft Ultimate #3 + residual 8: invitation-only nudge (never auto-fills; not on card/PNG)
              !hand.takeaway.trim()
                ? el('p', { className: 'muted takeawayNudge' }, nudgeSegments(ui.takeawayNudge))
                : null,
              // Soft I2b: quiet fact — screen only (never Copy / PNG / payload).
              // Sender-only: received `#/h/` already carries the note.
              hand.takeaway.trim() && !isReceivedFromLink()
                ? el('p', {
                    className: 'muted takeawayInLinkFact',
                    text: ui.takeawayInLinkFact,
                  })
                : null,
              explainOnly(
                el('p', {
                  className: 'muted takeawayHint',
                  text: ui.takeawayHint,
                }),
              ),
            ]),
          ])
        : null,
      heroOnlyHolesNoteBlock,
      ],
    ),
    // softGateBanner: shell-only (avoid double alertdialog on Summary)
    toolbar,
  ])
}


/**
 * takeaway-nudge-copy: screen-only markup so the nudge breaks only after a 「·」
 * (never mid-word, never a 「·」 at a line start). Each 「·」-separated segment
 * is a nowrap span that ends with its 「·」; whitespace between segments stays a
 * plain text node and a <wbr> marks the break where there is none (자리·라인).
 * textContent is unchanged.
 */
function nudgeSegments(text: string): (Node | string)[] {
  const parts = text.split('·')
  const nodes: (Node | string)[] = []
  parts.forEach((part, i) => {
    const last = i === parts.length - 1
    const lead = part.match(/^[ \t]+/)?.[0] ?? ''
    if (lead) nodes.push(lead)
    else if (i > 0) nodes.push(el('wbr'))
    nodes.push(el('span', { className: 'takeawayNudgeSeg', text: part.slice(lead.length) + (last ? '' : '·') }))
  })
  return nodes
}
